import { useCallback, useEffect, useMemo, useState } from "react";
import { hasZoomAxis, maxManualSpeedFor, type PtzDirection } from "@lms/shared";

import { Icon } from "./Icon.js";
import { TraceRecorder } from "./TraceRecorder.js";
import { ApiError } from "../api/client.js";
import type { ClusterState } from "../api/telemetry.js";
import type { Cluster } from "./ClusterCard.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La colonna PTZ, portata da `PTZJoystick.qml`.
 *
 * Stesso ordine dell'app desktop, che non e' casuale: prima le due soglie —
 * l'unica cosa che si guarda mentre si muove la testa — poi lo zoom, poi le
 * due schede MANUALE / AUTOMATICO.
 *
 * Il joystick e' un CERCHIO con quattro frecce sui bordi e il mozzo al
 * centro, non una griglia di nove bottoni: e' cosi' che si usa guardando il
 * video invece che i comandi.
 */

type Tab = "manual" | "auto";

export interface PtzTrace {
  id: string;
  name: string;
  waypoints: unknown[];
}

export function PtzPanel({
  cluster,
  telemetry,
  livePpm,
}: {
  cluster: Cluster;
  telemetry?: ClusterState | undefined;
  livePpm: number | undefined;
}) {
  const { t, reportLabels } = useI18n();
  const { api, can } = useSession();

  const [tab, setTab] = useState<Tab>("manual");
  const [speed, setSpeed] = useState(10);
  const [tiltSpeed, setTiltSpeed] = useState(10);
  const [traces, setTraces] = useState<PtzTrace[]>([]);
  const [activeTrace, setActiveTrace] = useState<string>("");
  const [patrol, setPatrol] = useState(false);
  const [autoStop, setAutoStop] = useState(0);
  /** Posizione del cursore dello zoom, 0-1. Il rig la posizione vera non la
   *  dice (su Pelco-D lo zoom non e' leggibile), quindi vale quella che
   *  l'operatore ha appena comandato — come nel .qml. */
  const [zoomValue, setZoomValue] = useState(0);
  const [busy, setBusy] = useState("");
  /** L'esito del report giornaliero. Dura piu' di un lampo: il messaggio dice
   *  quante rilevazioni sono finite dentro, ed e' da leggere. */
  const [toast, setToast] = useState<{ ok: boolean; text: string } | undefined>();
  const [error, setError] = useState("");
  const [activeDir, setActiveDir] = useState<PtzDirection | undefined>();
  /**
   * Continuo o a passo.
   *
   * Il passo e' uno scatto breve che si ferma da solo, e su un gesto corto e'
   * piu' sicuro del continuo: se lo stop si perde per strada, con il continuo
   * la testa non si ferma. Esiste pero' solo sul firmware Flexwatch (rig
   * ONVIF); sugli altri il movimento e' continuo e basta, e offrire la scelta
   * significherebbe offrire un comando che il server rifiuta.
   */
  const [ptzMode, setPtzMode] = useState<"continuous" | "step">("continuous");
  const hasStepMode = (cluster.cameraProtocol ?? "") === "onvif" || cluster.ptzModel === "indusvision";

  const maxSpeed = maxManualSpeedFor(cluster.ptzModel);
  const zoom = hasZoomAxis(cluster.ptzModel);
  const l1 = cluster.thresholdL1 ?? 0;
  const l2 = cluster.thresholdL2 ?? 0;
  const ppm = livePpm ?? 0;

  // Velocita' iniziale a meta' del massimo del rig: su un rig lento il default
  // storico di 10 vale 0,34 °/s, cioe' un asse che non sembra muoversi.
  useEffect(() => {
    setSpeed(Math.round(maxSpeed / 2));
    setTiltSpeed(Math.round(maxSpeed / 2));
  }, [maxSpeed]);

  const reloadTraces = useCallback(() => {
    void api
      .call<PtzTrace[]>("ptz-traces.list", { clusterId: cluster.id })
      .then(setTraces)
      .catch(() => {
        /* nessuna traccia o permesso mancante: la sezione resta vuota */
      });
    void api
      .call<Record<string, unknown>>("clusters.get", { id: cluster.id })
      .then((c) => setActiveTrace(String(c["activePtzTraceId"] ?? "")))
      .catch(() => {});
  }, [api, cluster.id]);

  useEffect(reloadTraces, [reloadTraces]);

  // I waypoint della traccia attiva, per mostrare il percorso mentre gira.
  // Si prendono dall'elenco gia' caricato invece di richiederli: la lista li
  // porta con se', e una seconda chiamata darebbe due copie che possono
  // divergere.
  const runningWaypoints = useMemo(() => {
    const tr = traces.find((x) => x.id === activeTrace);
    const wps = Array.isArray(tr?.waypoints) ? tr.waypoints : [];
    return wps.filter((w): w is Record<string, unknown> => !!w && typeof w === "object");
  }, [traces, activeTrace]);

  const segIndex = telemetry?.ptzSegIndex ?? -1;

  /**
   * Il conto alla rovescia del rientro.
   *
   * Prima di seguire il percorso la testa torna a casa, e per qualche secondo
   * non risponde ai comandi. Senza il numero quello e' un bottone che sembra
   * bloccato: e' l'unico momento in cui l'operatore non capisce cosa stia
   * facendo il rig. La durata e' quella configurata sull'impianto.
   */
  const homing = telemetry?.ptzHoming === true;
  const [homingLeft, setHomingLeft] = useState(0);
  useEffect(() => {
    if (!homing) {
      setHomingLeft(0);
      return undefined;
    }
    const total = Number(cluster.ptzHomeDurationMs) || 3000;
    const started = performance.now();
    const tick = () =>
      setHomingLeft(Math.max(0, Math.ceil((total - (performance.now() - started)) / 1000)));
    tick();
    const timer = setInterval(tick, 200);
    return () => clearInterval(timer);
  }, [homing, cluster.ptzHomeDurationMs]);

  // La ronda in corso la dice la telemetria, non il nostro interruttore: se
  // qualcun altro la ferma da un'altra postazione, questa deve accorgersene.
  useEffect(() => {
    if (telemetry?.ptzAutoMode !== undefined) setPatrol(telemetry.ptzAutoMode);
  }, [telemetry?.ptzAutoMode]);

  const call = useCallback(
    async (route: Parameters<typeof api.call>[0], body: unknown) => {
      try {
        await api.call(route, body);
        setError("");
        return true;
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
        return false;
      }
    },
    [api],
  );

  // Premi e tieni: il comando parte al `pointerdown` e lo STOP al rilascio.
  // Senza lo stop la testa continua finche' non interviene la protezione
  // anti-fuga del firmware — una quindicina di secondi che nessuno ha chiesto.
  const startMove = useCallback(
    (dir: PtzDirection) => {
      setActiveDir(dir);
      void call("ptz.command", {
        clusterId: cluster.id,
        command: dir,
        mode: hasStepMode ? ptzMode : "continuous",
        speed: dir === "up" || dir === "down" ? tiltSpeed : speed,
      });
    },
    [call, cluster.id, tiltSpeed, speed, hasStepMode, ptzMode],
  );

  const stopMove = useCallback(() => {
    setActiveDir(undefined);
    void call("ptz.stop", { clusterId: cluster.id });
  }, [call, cluster.id]);

  const hold = (dir: PtzDirection) => ({
    onPointerDown: () => startMove(dir),
    onPointerUp: () => stopMove(),
    onPointerLeave: () => {
      if (!activeDir) return;
      stopMove();
    },
  });

  /**
   * Le quattro frecce della tastiera, come nell'app desktop.
   *
   * Il ripetuto automatico si ignora: il movimento e' gia' continuo finche'
   * non arriva lo STOP, e ogni ripetizione sarebbe un comando in piu' sulla
   * stessa direzione. E si sta zitti se le frecce servono a chi sta scrivendo
   * in un campo — il nome di una traccia, per dirne una.
   */
  useEffect(() => {
    if (!can("controlPtz")) return undefined;

    const typing = (el: EventTarget | null) => {
      const t = el as HTMLElement | null;
      const tag = t?.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable;
    };
    const dirOf = (key: string): PtzDirection | undefined =>
      key === "ArrowUp"
        ? "up"
        : key === "ArrowDown"
          ? "down"
          : key === "ArrowLeft"
            ? "left"
            : key === "ArrowRight"
              ? "right"
              : undefined;

    const down = (e: KeyboardEvent) => {
      if (e.repeat || typing(e.target)) return;
      const dir = dirOf(e.key);
      if (!dir) return;
      e.preventDefault();
      startMove(dir);
    };
    const up = (e: KeyboardEvent) => {
      if (typing(e.target) || !dirOf(e.key)) return;
      e.preventDefault();
      stopMove();
    };
    // Se la finestra perde il fuoco con un tasto premuto il `keyup` non arriva
    // mai, e la testa resterebbe in movimento.
    const blur = () => stopMove();

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, [can, startMove, stopMove]);

  const togglePatrol = async () => {
    const next = !patrol;
    setPatrol(next);
    setBusy("patrol");
    const ok = await call("ptz.automode", { clusterId: cluster.id, enabled: next });
    // Riportare indietro l'interruttore: mostrarlo acceso su una ronda che non
    // e' partita e' peggio che non averlo.
    if (!ok) setPatrol(!next);
    setBusy("");
  };

  /** Cliccare quella gia' attiva la disattiva, come nell'app desktop: e'
   *  l'unico modo per togliere la traccia senza cancellarla. */
  const selectTrace = async (id: string) => {
    const next = activeTrace === id ? null : id;
    if (await call("clusters.update", { id: cluster.id, activePtzTraceId: next })) {
      setActiveTrace(next ?? "");
    }
  };

  const armAutoStop = async (hours: number) => {
    setAutoStop(hours);
    await call("ptz.setAutoStop", { clusterId: cluster.id, hours });
  };

  return (
    <aside className="ptz">
      {error && <p className="alert">{error}</p>}

      {/* Chi non puo' muovere la testa lo legge subito, invece di scoprirlo
          premendo. I comandi restano visibili: nasconderli farebbe pensare a
          un programma incompleto. */}
      {!can("controlPtz") && (
        <p className="ptz__locked">
          <Icon name="shield" size={14} />
          {t("access.ptzRestricted")}
        </p>
      )}

      {/* ── soglie L1/L2 e reset del relè DCS ─────────────────────────── */}
      <div className="ptz__thresholds">
        {[
          { lab: "L1", lim: l1, over: ppm >= l1 && l1 > 0 },
          { lab: "L2", lim: l2, over: ppm >= l2 && l2 > 0 },
        ].map((x) => (
          <div key={x.lab} className={`thr${x.over ? " thr--over" : ""}`}>
            <div className="thr__label">
              {t("ptz.threshold")} {x.lab}
            </div>
            <div className="thr__value">
              <i className="dot" />
              {Math.round(x.lim)}
            </div>
          </div>
        ))}
        {/* Il bottone c'e' come nell'app desktop, ma e' SPENTO: sul server non
            esiste una rotta per resettare il relè DCS. Nel C++ quella non e'
            una chiamata HTTP — e' un segnale interno dal bridge al worker
            (`SensorBridge::dcsRelayResetRequested`), classificato `internal` e
            quindi non esposto. Toglierlo nasconderebbe la mancanza; lasciarlo
            attivo darebbe un 404 in faccia all'operatore. */}
        <button
          className="dcs"
          title={`${t("ptz.resetDcs")} — non disponibile sul server`}
          disabled
        >
          <span className="dcs__glyph">↻</span>
          <span className="dcs__label">DCS</span>
        </button>
      </div>

      {/* ── zoom ───────────────────────────────────────────────────────── */}
      {/* Su un'ottica fissa il gruppo SPARISCE, invece di restare lì a mandare
          comandi che il firmware ignora e a mostrare una percentuale
          inventata. */}
      {zoom && (
        <div className="ptz__group">
          <div className="ptz__title">ZOOM</div>
          {/* Meno, cursore, piu': come nel .qml. Due bottoni grandangolo/tele
              sanno solo saltare ai due estremi, e su un'ottica motorizzata la
              posizione intermedia e' quella che si usa davvero. */}
          <div className="ptz__zoom">
            <button
              className="ptz__zoomStep"
              onClick={() => {
                const v = Math.max(0, Math.round((zoomValue - 0.1) * 100) / 100);
                setZoomValue(v);
                void call("ptz.zoom", { clusterId: cluster.id, value: v });
              }}
            >
              −
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={zoomValue}
              style={pct(zoomValue, 0, 1)}
              onChange={(e) => setZoomValue(Number(e.target.value))}
              onPointerUp={() => void call("ptz.zoom", { clusterId: cluster.id, value: zoomValue })}
              onKeyUp={() => void call("ptz.zoom", { clusterId: cluster.id, value: zoomValue })}
            />
            <button
              className="ptz__zoomStep"
              onClick={() => {
                const v = Math.min(1, Math.round((zoomValue + 0.1) * 100) / 100);
                setZoomValue(v);
                void call("ptz.zoom", { clusterId: cluster.id, value: v });
              }}
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* ── schede ─────────────────────────────────────────────────────── */}
      <div className="ptz__tabs">
        <button
          className={`ptztab${tab === "manual" ? " ptztab--on" : ""}`}
          onClick={() => setTab("manual")}
        >
          {t("ptz.tabManual")}
        </button>
        <button
          className={`ptztab${tab === "auto" ? " ptztab--on" : ""}`}
          onClick={() => setTab("auto")}
        >
          {t("ptz.tabAutomatic")}
        </button>
      </div>

      {tab === "manual" ? (
        <>
          {/* Il joystick è un CERCHIO con le frecce sui bordi, non una
              griglia: si usa guardando il video, non i comandi. */}
          <div className="joy">
            {/* Frecce e perno stanno DENTRO il cerchio: cosi' restano
                incollate al bordo comunque sia alta la colonna, invece di
                staccarsene quando il contenitore cresce. */}
            <div className="joy__circle">
            <button className={`joy__arrow joy__arrow--up${activeDir === "up" ? " on" : ""}`} {...hold("up")}>
              <Icon name="arrow-up" size={18} />
            </button>
            <button className={`joy__arrow joy__arrow--down${activeDir === "down" ? " on" : ""}`} {...hold("down")}>
              <Icon name="arrow-down" size={18} />
            </button>
            <button className={`joy__arrow joy__arrow--left${activeDir === "left" ? " on" : ""}`} {...hold("left")}>
              <Icon name="arrow-left" size={18} />
            </button>
            <button className={`joy__arrow joy__arrow--right${activeDir === "right" ? " on" : ""}`} {...hold("right")}>
              <Icon name="arrow-right" size={18} />
            </button>
            <div className={`joy__hub${telemetry?.ptzHoming ? " joy__hub--homing" : ""}`}>
              <i />
            </div>
            </div>
          </div>

          {/* Etichetta e bottoni sulla STESSA riga, come nel .qml: e' una
              scelta fra due, non una sezione. */}
          {hasStepMode && (
            <div className="ptz__inline">
              <span className="ptz__inlineLabel">{t("hdr.ptzMode")}</span>
              {(["continuous", "step"] as const).map((m) => (
                <button
                  key={m}
                  className={`ptz__modeBtn${ptzMode === m ? " ptz__modeBtn--on" : ""}`}
                  onClick={() => setPtzMode(m)}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          )}

          <div className="ptz__group">
            <div className="ptz__title">{t("focus.scanControls")}</div>
            {/* Etichetta, cursore, valore a destra: nel .qml il numero sta
                fuori dal testo perche' lo si legge mentre si trascina. */}
            <label className="ptz__speed">
              <span className="ptz__speedLabel">{t("focus.horizSpeed")}</span>
              <input
                type="range"
                min={1}
                max={maxSpeed}
                value={speed}
                style={pct(speed, 1, maxSpeed)}
                onChange={(e) => setSpeed(Number(e.target.value))}
              />
              <b className="ptz__speedValue">{speed}</b>
            </label>
            <label className="ptz__speed">
              <span className="ptz__speedLabel">{t("focus.vertSpeed")}</span>
              <input
                type="range"
                min={1}
                max={maxSpeed}
                value={tiltSpeed}
                style={pct(tiltSpeed, 1, maxSpeed)}
                onChange={(e) => setTiltSpeed(Number(e.target.value))}
              />
              <b className="ptz__speedValue">{tiltSpeed}</b>
            </label>
          </div>

          {/* Dove sta la testa, in cifre. Nel .qml sta qui e non
              nell'intestazione sopra il video: si legge mentre si tiene
              premuta una freccia, e chi guida guarda il video e questa
              colonna, non la barra del titolo. */}
          <div className="ptz__readout">
            <span className="ptz__readoutLabel">PAN</span>
            <span className="ptz__readoutValue">
              {telemetry?.ptzAngle === undefined ? "—" : `${telemetry.ptzAngle.toFixed(1)}\u00b0`}
            </span>
            <span className="ptz__readoutLabel">TILT</span>
            <span className="ptz__readoutValue">
              {telemetry?.ptzTilt === undefined ? "—" : `${telemetry.ptzTilt.toFixed(1)}\u00b0`}
            </span>
            <span className="ptz__readoutLabel">ZOOM</span>
            <span className="ptz__readoutValue">
              {telemetry?.ptzZoom === undefined ? "—" : String(Math.round(telemetry.ptzZoom))}
            </span>
          </div>

          <div className="ptz__row">
            <button onClick={() => void call("ptz.parking", { clusterId: cluster.id })}>
              <Icon name="home" size={14} />
              {t("focus.home")}
            </button>
            {/* Il report giornaliero sta ACCANTO a Home, e ha preso il posto
                del tergicristallo: nel .qml quel bottone e' stato sostituito
                da questo, e tenerlo qui sarebbe un comando che l'app desktop
                non ha piu'. Lo genera il server, il browser lo scarica. */}
            <button
              disabled={busy === "report"}
              onClick={() => {
                setBusy("report");
              void api
                .call<{ url: string; filename: string; leakCount?: number }>(
                  "reports.daily_leak_pdf",
                  { clusterId: cluster.id, labels: reportLabels() },
                )
                .then((r) => {
                  const a = document.createElement("a");
                  a.href = r.url;
                  a.download = r.filename;
                  a.click();
                  setError("");
                  const n = Number(r.leakCount) || 0;
                  setToast({
                    ok: true,
                    text: n > 0 ? t("focus.reportSaved", { p: n }) : t("focus.reportSavedEmpty"),
                  });
                })
                .catch((e: unknown) => {
                  setError(e instanceof ApiError ? e.message : String(e));
                  setToast({ ok: false, text: t("focus.reportFailed") });
                })
                .finally(() => {
                  setBusy("");
                  setTimeout(() => setToast(undefined), 5000);
                });
            }}
          >
              <Icon name="file-text" size={14} />
              {busy === "report" ? t("focus.reportBusy") : t("focus.report")}
            </button>
          </div>

          {/* Il parcheggio per il trasporto sta su una riga sua, larga tutto:
              non e' un comando di tutti i giorni come Home. */}
          <button
            className="ptz__wide"
            onClick={() => void call("ptz.parkForTransport", { clusterId: cluster.id })}
          >
            <Icon name="parking-circle" size={14} />
            {t("focus.parking")}
          </button>
        </>
      ) : (
        <>
          {/* La registrazione sta in cima, come nell'app desktop: e' quello
              per cui si apre questa scheda quando l'impianto e' nuovo. */}
          {can("recordTraces") && (
            <TraceRecorder
              clusterId={cluster.id}
              canControl={can("controlPtz")}
              angle={telemetry?.ptzAngle ?? 0}
              tilt={telemetry?.ptzTilt ?? 0}
              zoom={telemetry?.ptzZoom ?? 30}
              encPan={telemetry?.ptzEncPan ?? -1}
              encTilt={telemetry?.ptzEncTilt ?? -1}
              hold={hold}
              onSaved={(id) => {
                setActiveTrace(id);
                reloadTraces();
              }}
            />
          )}

          {/* Durante la ronda l'elenco delle tracce sparisce e al suo posto
              c'e' quella in esecuzione: cambiare percorso mentre la testa lo
              sta facendo non e' un comando che si offre per sbaglio. */}
          {!patrol && (
            <div className="ptz__group">
              <div className="ptz__title">{t("ptz.savedTraces")}</div>
              {traces.length === 0 && <p className="muted small">{t("ptz.noTracesSaved")}</p>}
              <ul className="tracelist">
                {traces.map((tr) => (
                  <li key={tr.id}>
                    <button
                      className={`link${activeTrace === tr.id ? " on" : ""}`}
                      onClick={() => void selectTrace(tr.id)}
                    >
                      <Icon name={activeTrace === tr.id ? "check-circle" : "play"} size={12} />
                      {tr.name}
                      <span className="muted small">
                        {Array.isArray(tr.waypoints) ? tr.waypoints.length : 0} {t("ptz.points")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* I waypoint del percorso in corso, con quello verso cui la testa
              sta andando in evidenza. */}
          {patrol && runningWaypoints.length > 0 && (
            <div className="ptz__group">
              <div className="ptz__title">{t("ptz.savedTraces")}</div>
              <ul className="wplist">
                {runningWaypoints.map((w, i) => (
                  <li
                    key={`${String(w["label"] ?? i)}-${i}`}
                    className={i === segIndex ? "wplist__on" : undefined}
                  >
                    <b>{String(w["label"] ?? i + 1)}</b>
                    <span>
                      P{Number(w["angle"] ?? 0).toFixed(0)}° T{Number(w["tilt"] ?? 0).toFixed(0)}°
                    </span>
                    <span className="muted small">{Number(w["dwellSeconds"] ?? 0)}s</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="ptz__group">
            <div className="ptz__title">{t("ptz.autoShutoff")}</div>
            <div className="ptz__row ptz__row--wrap">
              <span className="muted small">{t("ptz.autoStopPatrol")}</span>
              <select
                value={autoStop}
                onChange={(e) => void armAutoStop(Number(e.target.value))}
              >
                {[0, 1, 2, 4, 8, 12, 24].map((h) => (
                  <option key={h} value={h}>
                    {h === 0 ? t("ptz.autoStop.off") : `${h}h`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ultimo della colonna, incollato in fondo come nel .qml — li' c'e'
              un `Item { Layout.fillHeight: true }` apposta. Senza una traccia
              attiva non c'e' niente da far percorrere: il bottone lo dice
              invece di partire e fermarsi subito. */}
          <button
            className={`ptz__wide ptz__wide--last${patrol ? " btn--primary" : ""}${homing ? " ptz__wide--homing" : ""}`}
            disabled={busy === "patrol" || homing || (!patrol && !activeTrace)}
            onClick={() => void togglePatrol()}
          >
            <Icon name={patrol ? "square" : "play"} size={14} />
            {homing
              ? `${t("ptz.homing")}${homingLeft > 0 ? ` ${homingLeft}s` : ""}`
              : patrol
                ? t("ptz.stopPatrol")
                : activeTrace
                  ? t("ptz.startPatrol")
                  : t("ptz.noTrace")}
          </button>
        </>
      )}
      {toast && <p className={`ptz__toast${toast.ok ? "" : " ptz__toast--ko"}`}>{toast.text}</p>}
    </aside>
  );
}

/**
 * Quanto e' percorso un cursore, in percentuale, per il foglio di stile.
 *
 * Lo `Slider` di Qt colora di verde la parte gia' percorsa e lascia grigio il
 * resto; in HTML il solco e' un elemento solo, quindi la posizione va passata
 * come variabile e il gradiente la usa per il taglio.
 */
export function pct(value: number, min: number, max: number): React.CSSProperties {
  const span = max - min;
  const p = span <= 0 ? 0 : ((value - min) / span) * 100;
  return { "--pct": `${Math.max(0, Math.min(100, p))}%` } as React.CSSProperties;
}
